import React, { createContext, useContext, useRef } from "react"
import { fetchQuery, graphql } from "relay-runtime"
import { create, StoreApi, UseBoundStore, useStore } from "zustand"
import {
  fetchTreatmentPlanTemplatesQuery,
  fetchTreatmentPlanTemplatesQuery$data,
  TreatmentPlanTemplateFilterInput,
} from "@/modules/patients/services/__generated__/fetchTreatmentPlanTemplatesQuery.graphql"
import { RelayEnvironment } from "@/relay-environment"
import { Country, Language } from "@/types/__generated__/union-types"

export type TreatmentPlanTemplatesData = fetchTreatmentPlanTemplatesQuery$data["treatmentPlanTemplates"][0]
type TreatmentPlanTemplatesLanguage = fetchTreatmentPlanTemplatesQuery$data["treatmentPlanTemplates"][0]["languages"][0]

export interface TreatmentPlanTemplateStoreData extends TreatmentPlanTemplatesLanguage {
  name: string
  id: string
  amountOfDays: TreatmentPlanTemplatesData["amountOfDays"]
  asynchronousQuestionnaireUsage: TreatmentPlanTemplatesData["asynchronousQuestionnaireUsage"]
}

interface TreatmentPlanTemplatesStore {
  treatmentPlanTemplates: Map<Language, TreatmentPlanTemplateStoreData[]> | null
  setTreatmentPlanTemplates: (treatmentPlanTemplates: Map<Language, TreatmentPlanTemplateStoreData[]>) => void
}

const createTreatmentPlanTemplatesStore = () => {
  return create<TreatmentPlanTemplatesStore>((set) => ({
    treatmentPlanTemplates: null,
    setTreatmentPlanTemplates: (treatmentPlanTemplates) => {
      set({ treatmentPlanTemplates: treatmentPlanTemplates })
    },
  }))
}

type TreatmentPlanTemplatesStoreType = ReturnType<typeof createTreatmentPlanTemplatesStore>

export const TreatmentPlanTemplatesStoreContext = createContext<TreatmentPlanTemplatesStoreType | null>(null)

type TreatmentPlanTemplatesStoreProviderProps = React.PropsWithChildren

export const TreatmentPlanTemplatesStoreProvider = ({ children }: TreatmentPlanTemplatesStoreProviderProps) => {
  const storeRef = useRef<TreatmentPlanTemplatesStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createTreatmentPlanTemplatesStore()
  }
  return (
    <TreatmentPlanTemplatesStoreContext.Provider value={storeRef.current}>
      {children}
    </TreatmentPlanTemplatesStoreContext.Provider>
  )
}

export function useTreatmentPlanTemplatesStoreContext<T>(selector: (state: TreatmentPlanTemplatesStore) => T): T {
  const store = useContext(TreatmentPlanTemplatesStoreContext)
  if (!store) throw new Error("Missing TreatmentPlanTemplatesStoreProvider in the tree")
  return useStore(store, selector)
}

export function fetchTreatmentPlanTemplates(
  patientCountry: Country,
  treatmentPlanTemplatesStore: UseBoundStore<StoreApi<TreatmentPlanTemplatesStore>>,
) {
  const filters: TreatmentPlanTemplateFilterInput = {
    countries: {
      some: {
        in: [patientCountry],
      },
    },
  }
  fetchQuery<fetchTreatmentPlanTemplatesQuery>(
    RelayEnvironment,
    graphql`
      query fetchTreatmentPlanTemplatesQuery($filters: TreatmentPlanTemplateFilterInput) {
        treatmentPlanTemplates(where: $filters) {
          id
          name
          amountOfDays
          countries
          asynchronousQuestionnaireUsage
          languages {
            language
            title
            recommendedTreatment
            followUpInstructions
            synonym
          }
        }
      }
    `,
    { filters: filters },
  ).subscribe({
    next: (data) => {
      const treatmentPlanTemplates: Map<Language, TreatmentPlanTemplateStoreData[]> = new Map()

      data.treatmentPlanTemplates
        .flatMap((item) =>
          item.languages.map((language) => {
            const result: TreatmentPlanTemplateStoreData = {
              followUpInstructions: language.followUpInstructions,
              language: language.language,
              recommendedTreatment: language.recommendedTreatment,
              synonym: language.synonym,
              title: language.title,
              name: item.name,
              id: item.id,
              amountOfDays: item.amountOfDays,
              asynchronousQuestionnaireUsage: item.asynchronousQuestionnaireUsage,
            }
            return result
          }),
        )
        .flat(1)
        .forEach((treatmentPlanTemplate) => {
          const key = treatmentPlanTemplate.language
          if (!treatmentPlanTemplates.has(key)) {
            treatmentPlanTemplates.set(key, [treatmentPlanTemplate])
          } else {
            const currentValue = treatmentPlanTemplates.get(key)
            if (currentValue) {
              treatmentPlanTemplates.set(key, [...currentValue, treatmentPlanTemplate])
            } else {
              treatmentPlanTemplates.set(key, [treatmentPlanTemplate])
            }
          }
        })
      treatmentPlanTemplatesStore.getState().setTreatmentPlanTemplates(treatmentPlanTemplates)
    },
  })
}
